(() => {
  "use strict";

  const addModes = new Set(["ready_stock", "standard_mto"]);
  const inquiryModes = new Set(["personal_fit", "request_availability"]);
  const knownModes = new Set([...addModes, ...inquiryModes, "archive"]);
  const maxGalleryItems = 5;
  const galleryRoles = new Set(["hero", "on-body", "exterior", "surface", "context"]);

  const cleanText = (value, maxLength = 160) => String(value || "")
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

  const normalizeId = (value) => cleanText(value, 96).split("/").pop().replace(/\D/g, "");
  const strictNumericId = (value) => {
    const candidate = cleanText(value, 96);
    return /^\d+$/.test(candidate) ? candidate : "";
  };

  const parseData = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || "null");
    } catch (error) {
      return null;
    }
  };

  const initializeProduct = (root) => {
    if (root.dataset.ensoInitialized === "true") return;
    root.dataset.ensoInitialized = "true";

    const data = parseData(root, "[data-enso-product-json]");
    const optionDefinitions = Array.isArray(data?.options)
      ? data.options.map((option) => ({
        position: Number(option?.position),
        name: cleanText(option?.name, 80),
        values: Array.isArray(option?.values) ? option.values.map((value) => ({
          id: normalizeId(value?.id),
          name: cleanText(value?.name, 120),
          url: cleanText(value?.url, 2048),
          available: value?.available === true
        })).filter((value) => value.id && value.name) : []
      })).filter((option) => option.position >= 1 && option.position <= 3 && option.name)
        .sort((left, right) => left.position - right.position)
      : [];
    const optionIndexByPosition = new Map(
      optionDefinitions.map((option, index) => [option.position, index])
    );
    const optionCount = Number(data?.optionCount);
    const resolverSupported = data?.resolverSupported === true
      && Number.isInteger(optionCount)
      && optionCount >= 0
      && optionCount <= 3
      && optionDefinitions.length === optionCount;
    const surfaces = Array.isArray(data?.surfaces)
      ? data.surfaces.map((surface) => ({
        id: normalizeId(surface?.id),
        name: cleanText(surface?.name, 120)
      })).filter((surface) => surface.id && surface.name)
      : [];
    const surfaceById = new Map(surfaces.map((surface) => [surface.id, surface]));
    const variants = Array.isArray(data?.variants)
      ? data.variants.map((variant) => {
        const rawPriceMinor = variant?.priceMinor;
        return {
          ...variant,
          id: normalizeId(variant?.id),
          surfaceId: normalizeId(variant?.surfaceId),
          surface: cleanText(variant?.surface, 120),
          size: cleanText(variant?.size, 80),
          options: Array.isArray(variant?.options)
            ? variant.options.slice(0, optionCount).map((value) => cleanText(value, 120))
            : [],
          fulfilmentMode: cleanText(variant?.fulfilmentMode, 40),
          availabilityLabel: cleanText(variant?.availabilityLabel, 160),
          leadTime: cleanText(variant?.leadTime, 120),
          price: cleanText(variant?.price, 80),
          priceMinor: rawPriceMinor === null || rawPriceMinor === undefined || rawPriceMinor === ""
            ? Number.NaN
            : Number(rawPriceMinor)
        };
      })
      : [];
    const productMediaIds = new Set(
      (Array.isArray(data?.productMediaIds) ? data.productMediaIds : []).map(normalizeId).filter(Boolean)
    );
    const labels = data?.labels || {};
    const surfaceRequired = Number(data?.surfacePosition) > 0;
    const sizeRequired = Number(data?.sizePosition) > 0;
    const sizeLabel = cleanText(data?.sizeOptionName || "Size", 80);
    const selectionNoun = /\bsize\b/i.test(sizeLabel) ? "size" : sizeLabel.toLocaleLowerCase();
    const ids = variants.map((variant) => variant.id).filter(Boolean);
    const contractIsValid = data?.contractValid === true
      && resolverSupported
      && variants.length > 0
      && ids.length === variants.length
      && new Set(ids).size === ids.length
      && variants.every((variant) => variant.options.length === optionCount)
      && (!surfaceRequired || surfaces.length > 0)
      && variants.every((variant) => !surfaceRequired || surfaceById.has(variant.surfaceId));

    const normalizeMediaItem = (item) => {
      const mediaId = normalizeId(item?.mediaId);
      const role = cleanText(item?.role, 32);
      const src = cleanText(item?.src, 2048);
      if (!mediaId || !productMediaIds.has(mediaId) || !galleryRoles.has(role) || !src) return null;
      return {
        mediaId,
        role,
        label: cleanText(item?.label, 80) || role,
        alt: cleanText(item?.alt, 240),
        src,
        fallbackSrc: cleanText(item?.fallbackSrc, 2048),
        srcset: cleanText(item?.srcset, 6000),
        sizes: cleanText(item?.sizes, 600)
      };
    };

    const runtimeGalleries = new Map();
    (Array.isArray(data?.galleries) ? data.galleries : []).forEach((entry) => {
      const surfaceId = normalizeId(entry?.surfaceId);
      if (!surfaceById.has(surfaceId)) return;
      const source = entry?.source === "metaobject" ? "metaobject" : "legacy";
      const rawItems = Array.isArray(entry?.items) ? entry.items : [];
      const seen = new Set();
      let items = rawItems.map(normalizeMediaItem).filter((item) => {
        if (!item || seen.has(item.mediaId)) return false;
        seen.add(item.mediaId);
        return true;
      }).slice(0, maxGalleryItems);
      const fallbackHero = normalizeMediaItem(entry?.fallbackHero);
      const metaContractValid = source !== "metaobject"
        || (entry?.contractValid === true
          && items.length >= 1
          && items.length <= maxGalleryItems
          && items[0]?.role === "hero"
          && new Set(items.map((item) => item.role)).size === items.length);
      if (!metaContractValid) items = [];
      if (items[0]?.role !== "hero" && fallbackHero && !seen.has(fallbackHero.mediaId)) {
        items.unshift(fallbackHero);
      }
      if (!items.length && fallbackHero) items = [fallbackHero];
      runtimeGalleries.set(surfaceId, {
        surfaceId,
        surface: surfaceById.get(surfaceId)?.name || cleanText(entry?.surface, 120),
        source,
        items: items.slice(0, maxGalleryItems),
        fallbackHero,
        broken: new Set()
      });
    });

    const sizeList = root.querySelector("[data-enso-size-options] .enso-object-product__size-list");
    if (sizeList) {
      const numericCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
      [...sizeList.children]
        .sort((left, right) => numericCollator.compare(
          left.querySelector("input")?.value || "",
          right.querySelector("input")?.value || ""
        ))
        .forEach((item) => sizeList.append(item));
    }

    const optionInputs = [...root.querySelectorAll("input[type='radio'][data-enso-option-position]")];
    const surfaceInputs = optionInputs.filter((input) => (
      Number(input.dataset.ensoOptionPosition) === Number(data?.surfacePosition)
    ));
    const sizeInputs = optionInputs.filter((input) => (
      Number(input.dataset.ensoOptionPosition) === Number(data?.sizePosition)
    ));
    const fitInputs = [...root.querySelectorAll("[data-enso-fit-options] input[type='radio']")];
    const surfaceCards = [...root.querySelectorAll("[data-enso-surface-card]")];
    const fitOptions = root.querySelector("[data-enso-fit-options]");
    const sizeOptions = root.querySelector("[data-enso-size-options]");
    const fitSummary = root.querySelector("[data-enso-fit-summary]");
    const price = root.querySelector("[data-enso-product-price]");
    const confirmation = root.querySelector("[data-enso-confirmation]");
    const form = root.querySelector(".enso-object-product__form");
    const variantInput = root.querySelector("[data-enso-variant-id]");
    const leadProperty = root.querySelector("[data-enso-lead-property]");
    const addAction = root.querySelector("[data-enso-add]");
    const addLabel = root.querySelector("[data-enso-add-label]");
    const inquiryAction = root.querySelector("[data-enso-inquiry-action]");
    const inquiryLabel = root.querySelector("[data-enso-inquiry-label]");
    const actionNote = root.querySelector("[data-enso-action-note]");
    const productError = root.querySelector("[data-enso-product-error]");
    const whatsappAction = root.querySelector("[data-enso-whatsapp]");
    const guidanceInquiryAction = root.querySelector("[data-enso-guidance-inquiry]");
    const contextDialog = root.querySelector("[data-enso-context-dialog]");
    const contextEyebrow = root.querySelector("[data-enso-context-eyebrow]");
    const contextTitle = root.querySelector("[data-enso-context-title]");
    const contextBody = root.querySelector("[data-enso-context-body]");
    const contextFoot = root.querySelector("[data-enso-context-foot]");
    const contextFullLink = root.querySelector("[data-enso-context-full-link]");
    const contextClose = root.querySelector("[data-enso-context-close]");
    const contextTriggers = [...root.querySelectorAll("[data-enso-context-open]")];
    const contextTemplates = new Map(
      [...root.querySelectorAll("template[data-enso-context-template]")]
        .map((template) => [cleanText(template.dataset.ensoContextTemplate, 32), template])
        .filter(([key]) => key)
    );
    const galleryElement = root.querySelector("[data-enso-gallery]");
    const galleryPrevious = root.querySelector("[data-enso-gallery-prev]");
    const galleryNext = root.querySelector("[data-enso-gallery-next]");
    const galleryStatus = root.querySelector("[data-enso-gallery-status]");
    const galleryDots = root.querySelector("[data-enso-gallery-dots]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const emptyOptionTuple = () => optionDefinitions.map(() => "");
    const normalizedOptionTuple = (values) => optionDefinitions.map((option, index) => {
      const candidate = cleanText(values?.[index], 120);
      return option.values.some((value) => value.name === candidate) ? candidate : "";
    });
    const variantsForOptions = (values) => {
      const tuple = normalizedOptionTuple(values);
      return variants.filter((variant) => optionDefinitions.every((option, index) => (
        !tuple[index] || variant.options[option.position - 1] === tuple[index]
      )));
    };
    const surfaceNameForId = (surfaceId) => surfaceById.get(surfaceId)?.name || "";
    const surfaceIdForName = (surfaceName) => surfaces.find((surface) => (
      surface.name === surfaceName
    ))?.id || "";
    const tupleWithSurfaceAndSize = (surfaceId, size, baseTuple) => {
      const tuple = normalizedOptionTuple(baseTuple);
      const surfaceIndex = optionIndexByPosition.get(Number(data?.surfacePosition));
      const sizeIndex = optionIndexByPosition.get(Number(data?.sizePosition));
      if (surfaceIndex !== undefined) tuple[surfaceIndex] = surfaceNameForId(surfaceId);
      if (sizeIndex !== undefined) tuple[sizeIndex] = cleanText(size, 120);
      return tuple;
    };
    const variantsForSurface = (surfaceId, baseTuple = emptyOptionTuple()) => (
      variantsForOptions(tupleWithSurfaceAndSize(surfaceId, "", baseTuple))
    );
    const candidatesForTuple = (surfaceId, size, baseTuple) => (
      variantsForOptions(tupleWithSurfaceAndSize(surfaceId, size, baseTuple))
    );
    const exactVariantForOptions = (values) => {
      const tuple = normalizedOptionTuple(values);
      if (tuple.some((value) => !value)) return null;
      const candidates = variantsForOptions(tuple);
      return candidates.length === 1 && candidates[0].id ? candidates[0] : null;
    };
    let selectedOptionValues = emptyOptionTuple();
    const exactVariant = (surfaceId, size, baseTuple = selectedOptionValues) => (
      exactVariantForOptions(tupleWithSurfaceAndSize(surfaceId, size, baseTuple))
    );
    const variantHasUniqueTuple = (variant) => Boolean(variant?.id
      && exactVariantForOptions(variant.options)?.id === variant.id);

    const firstValidSurfaceId = () => surfaces.find((surface) => (
      variantsForSurface(surface.id).some(variantHasUniqueTuple)
    ))?.id || "";

    const stateFromLocation = () => {
      const url = new URL(window.location.href);
      const requestedValues = url.searchParams.getAll("variant");
      const requestedId = requestedValues.length === 1 ? strictNumericId(requestedValues[0]) : "";
      const requestedVariants = requestedId
        ? variants.filter((variant) => variant.id === requestedId && variantHasUniqueTuple(variant))
        : [];
      if (requestedVariants.length === 1) {
        return {
          options: normalizedOptionTuple(requestedVariants[0].options),
          surfaceId: requestedVariants[0].surfaceId,
          size: requestedVariants[0].size,
          exact: requestedVariants[0]
        };
      }
      const optionTuple = emptyOptionTuple();
      const requestedOptionValues = url.searchParams.getAll("option_values");
      if (requestedOptionValues.length === 1) {
        requestedOptionValues[0].split(",").map(normalizeId).filter(Boolean).forEach((valueId) => {
          optionDefinitions.forEach((option, index) => {
            const matchedValue = option.values.find((value) => value.id === valueId);
            if (matchedValue) optionTuple[index] = matchedValue.name;
          });
        });
      } else {
        optionInputs.filter((input) => input.checked).forEach((input) => {
          const index = optionIndexByPosition.get(Number(input.dataset.ensoOptionPosition));
          if (index !== undefined) optionTuple[index] = cleanText(input.value, 120);
        });
      }
      const configuredDefault = normalizeId(data?.defaultSurfaceId);
      const defaultSurfaceId = surfaceById.has(configuredDefault)
        && variantsForSurface(configuredDefault).some(variantHasUniqueTuple)
        ? configuredDefault
        : firstValidSurfaceId();
      const surfaceIndex = optionIndexByPosition.get(Number(data?.surfacePosition));
      const sizeIndex = optionIndexByPosition.get(Number(data?.sizePosition));
      if (surfaceIndex !== undefined && !optionTuple[surfaceIndex]) {
        optionTuple[surfaceIndex] = surfaceNameForId(defaultSurfaceId);
      }
      const normalizedTuple = normalizedOptionTuple(optionTuple);
      return {
        options: normalizedTuple,
        surfaceId: surfaceIndex === undefined ? "" : surfaceIdForName(normalizedTuple[surfaceIndex]),
        size: sizeIndex === undefined ? "" : normalizedTuple[sizeIndex],
        exact: exactVariantForOptions(normalizedTuple)
      };
    };

    const initialState = stateFromLocation();
    selectedOptionValues = initialState.options;
    let selectedSurfaceId = initialState.surfaceId;
    let selectedSize = initialState.size;
    let selectedFit = fitInputs.find((input) => input.checked)?.value || "";
    let lastExact = initialState.exact;
    let stateGeneration = 0;
    let currentGallerySurfaceId = normalizeId(galleryElement?.dataset.ensoCurrentSurfaceId)
      || selectedSurfaceId;
    let currentGalleryPosition = 0;

    const selectOptionTuple = (values) => {
      selectedOptionValues = normalizedOptionTuple(values);
      const surfaceIndex = optionIndexByPosition.get(Number(data?.surfacePosition));
      const sizeIndex = optionIndexByPosition.get(Number(data?.sizePosition));
      selectedSurfaceId = surfaceIndex === undefined
        ? ""
        : surfaceIdForName(selectedOptionValues[surfaceIndex]);
      selectedSize = sizeIndex === undefined ? "" : selectedOptionValues[sizeIndex];
    };

    const setError = (message = "") => {
      if (!productError) return;
      productError.textContent = message;
      productError.hidden = !message;
    };

    const priceForSurface = (surfaceId, size = "") => {
      const group = candidatesForTuple(surfaceId, size, selectedOptionValues)
        .filter((variant) => Number.isFinite(variant.priceMinor) && variant.price)
        .sort((left, right) => left.priceMinor - right.priceMinor);
      if (!group.length) return "";
      return group[0].priceMinor === group[group.length - 1].priceMinor
        ? group[0].price
        : `FROM ${group[0].price}`;
    };

    const setSelectedStates = () => {
      optionInputs.forEach((input) => {
        const optionIndex = optionIndexByPosition.get(Number(input.dataset.ensoOptionPosition));
        const isSelected = optionIndex !== undefined && input.value === selectedOptionValues[optionIndex];
        input.checked = isSelected;
        const label = input.closest("label");
        if (isSelected) label?.setAttribute("data-selected", "true");
        else label?.removeAttribute("data-selected");
      });
      fitInputs.forEach((input) => {
        const isSelected = input.value === selectedFit;
        input.checked = isSelected;
        const label = input.closest("label");
        if (isSelected) label?.setAttribute("data-selected", "true");
        else label?.removeAttribute("data-selected");
      });
    };

    const syncSizeOptions = () => {
      optionInputs.forEach((input) => {
        const optionIndex = optionIndexByPosition.get(Number(input.dataset.ensoOptionPosition));
        if (optionIndex === undefined) return;
        const candidateTuple = [...selectedOptionValues];
        candidateTuple[optionIndex] = cleanText(input.value, 120);
        const exists = variantsForOptions(candidateTuple).length > 0;
        input.disabled = !exists;
        const label = input.closest("label");
        label?.toggleAttribute("data-unavailable", !exists);
        if (label) label.hidden = false;
      });
      if (sizeOptions) sizeOptions.hidden = false;
    };

    const renderSurfacePrices = () => {
      surfaceCards.forEach((card) => {
        const surfaceId = normalizeId(card.dataset.ensoSurfaceId);
        const output = card.querySelector("[data-enso-surface-price]");
        if (!output) return;
        output.textContent = priceForSurface(surfaceId, selectedSize);
        output.hidden = !output.textContent;
      });
    };

    const updateAddress = (variant, mode = "replace") => {
      if (mode === "none" || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      const hadSelectionParam = url.searchParams.has("variant") || url.searchParams.has("option_values");
      url.searchParams.delete("variant");
      url.searchParams.delete("option_values");
      if (variant?.id) {
        url.searchParams.set("variant", variant.id);
      } else {
        const optionValueIds = optionDefinitions.map((option, index) => (
          option.values.find((value) => value.name === selectedOptionValues[index])?.id || ""
        )).filter(Boolean);
        if (optionValueIds.length && (mode !== "initial" || hadSelectionParam)) {
          url.searchParams.set("option_values", optionValueIds.join(","));
        }
      }
      const previousState = window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};
      const nextHistoryState = {
        ...previousState,
        ensoObject: {
          handle: cleanText(data?.productHandle, 120),
          options: [...selectedOptionValues]
        }
      };
      window.history.replaceState(nextHistoryState, "", url);
    };

    const sameOriginSource = (variant = null) => {
      const source = new URL(window.location.href);
      source.hash = "";
      if (variant?.id) source.searchParams.set("variant", variant.id);
      else source.searchParams.delete("variant");
      return source.href;
    };

    const inquiryHref = (variant, need) => {
      if (!variant?.id || !inquiryModes.has(need)) return data.inquiryUrl;
      const url = new URL(data.inquiryUrl, window.location.origin);
      url.searchParams.set("object", cleanText(data.publicName, 120));
      url.searchParams.set("handle", cleanText(data.productHandle, 120));
      if (selectedSurfaceId) url.searchParams.set("surface", surfaceById.get(selectedSurfaceId)?.name || "");
      if (selectedSize) {
        url.searchParams.set("size", cleanText(selectedSize, 80));
        url.searchParams.set("size_label", sizeLabel);
      }
      if (selectedFit) url.searchParams.set("fit", cleanText(selectedFit, 120));
      url.searchParams.set("variant", variant.id);
      url.searchParams.set("need", need);
      url.searchParams.set("mode", cleanText(variant.fulfilmentMode, 40));
      url.searchParams.set("surface_required", surfaceRequired ? "1" : "0");
      url.searchParams.set("size_required", sizeRequired ? "1" : "0");
      url.searchParams.set("source", sameOriginSource(variant));
      return url.href;
    };

    const guidanceInquiryHref = (variant = null) => {
      const url = new URL(data.inquiryUrl, window.location.origin);
      url.searchParams.set("need", "configuration_guidance");
      url.searchParams.set("object", cleanText(data.publicName, 120));
      url.searchParams.set("handle", cleanText(data.productHandle, 120));
      const surfaceName = surfaceById.get(selectedSurfaceId)?.name || "";
      if (surfaceName) url.searchParams.set("surface", surfaceName);
      if (selectedSize) {
        url.searchParams.set("size", cleanText(selectedSize, 80));
        url.searchParams.set("size_label", sizeLabel);
      }
      if (selectedFit) url.searchParams.set("fit", cleanText(selectedFit, 120));
      if (variant?.id) {
        url.searchParams.set("variant", variant.id);
        url.searchParams.set("mode", cleanText(variant.fulfilmentMode, 40));
      }
      url.searchParams.set("surface_required", surfaceRequired ? "1" : "0");
      url.searchParams.set("size_required", sizeRequired ? "1" : "0");
      url.searchParams.set("source", sameOriginSource(variant));
      return url.href;
    };

    const resetActions = () => {
      if (variantInput) variantInput.value = "";
      if (leadProperty) {
        leadProperty.value = "";
        leadProperty.disabled = true;
      }
      if (addAction) {
        addAction.hidden = false;
        addAction.disabled = true;
      }
      if (inquiryAction) inquiryAction.hidden = true;
    };

    const deriveCommerceState = () => {
      if (!contractIsValid) {
        return {
          exact: null,
          action: "unavailable",
          label: labels.unavailable || "CONFIGURATION UNAVAILABLE",
          note: "This configuration is unavailable."
        };
      }
      const missingOptionIndex = selectedOptionValues.findIndex((value) => !value);
      if (missingOptionIndex >= 0) {
        const missingOption = optionDefinitions[missingOptionIndex];
        const missingName = missingOption?.name || "configuration";
        const missingNoun = missingOption?.position === Number(data?.sizePosition)
          ? `a ${selectionNoun}`
          : missingOption?.position === Number(data?.surfacePosition)
            ? "a surface"
            : cleanText(missingName, 80).toLocaleLowerCase();
        let selectionLabel = `SELECT ${cleanText(missingName, 80).toLocaleUpperCase()}`;
        if (missingOption?.position === Number(data?.surfacePosition)) {
          selectionLabel = labels.selectSurface || "SELECT A SURFACE";
        } else if (missingOption?.position === Number(data?.sizePosition)) {
          const configuredLabel = cleanText(labels.selectSize, 120);
          const defaultLabel = `SELECT A ${selectionNoun.toLocaleUpperCase()}`;
          selectionLabel = configuredLabel && configuredLabel !== "SELECT A SIZE"
            ? configuredLabel
            : defaultLabel;
        }
        return {
          exact: null,
          action: "select",
          label: selectionLabel,
          note: `Choose ${missingNoun} to continue.`
        };
      }

      const exact = exactVariantForOptions(selectedOptionValues);
      if (!exact?.id) {
        return {
          exact: null,
          action: "unavailable",
          label: labels.unavailable || "CONFIGURATION UNAVAILABLE",
          note: "This configuration is unavailable."
        };
      }
      const unavailable = () => ({
        exact,
        action: "unavailable",
        label: labels.unavailable || "CONFIGURATION UNAVAILABLE",
        note: "This configuration is unavailable."
      });
      if (!knownModes.has(exact.fulfilmentMode)) {
        return unavailable("Fulfilment data is incomplete for this configuration.");
      }
      if (exact.sizeRequired === true && !sizeRequired) {
        return unavailable("A required size option is not configured for this product.");
      }
      if (exact.fulfilmentMode === "archive") {
        return unavailable("This configuration is view only.");
      }
      if (inquiryModes.has(exact.fulfilmentMode)) {
        if (exact.inquiryContractValid !== true) {
          return unavailable("Inquiry availability data is incomplete for this configuration.");
        }
        return {
          exact,
          action: "inquiry",
          need: exact.fulfilmentMode,
          label: labels.request || "REQUEST AVAILABILITY",
          note: exact.fulfilmentMode === "personal_fit"
            ? "Personal fit"
            : "Not currently available online"
        };
      }
      if (!addModes.has(exact.fulfilmentMode)) {
        return unavailable("This configuration is not available for acquisition.");
      }
      if (exact.commerceApproved !== true || !exact.availabilityLabel) {
        return unavailable("Commerce approval or availability data is incomplete for this configuration.");
      }
      if (exact.fulfilmentMode === "ready_stock") {
        if (exact.readyContractValid !== true) {
          return unavailable("Ready-stock inventory data is incomplete for this configuration.");
        }
        if (exact.readyInStock === true && exact.available === true) {
          return {
            exact,
            action: "add",
            label: labels.add || "ADD TO BAG",
            note: "Ready stock"
          };
        }
        if (exact.readyUnavailableValid === true && exact.available === false) {
          return {
            exact,
            action: "inquiry",
            need: "request_availability",
            label: labels.request || "REQUEST AVAILABILITY",
            note: "Not currently available online"
          };
        }
        return unavailable("Ready-stock availability data is contradictory for this configuration.");
      }
      if (!exact.leadTime || exact.mtoContractValid !== true) {
        return unavailable("Made-to-order lead time or inventory policy is incomplete for this configuration.");
      }
      if (exact.available === true) {
        return {
          exact,
          action: "add",
          label: labels.add || "ADD TO BAG",
          note: `Made to order · ${exact.leadTime}`
        };
      }
      return {
        exact,
        action: "inquiry",
        need: "request_availability",
        label: labels.request || "REQUEST AVAILABILITY",
        note: "Not currently available online"
      };
    };

    const renderConfirmation = (exact) => {
      if (!confirmation) return;
      if (!exact?.id) {
        confirmation.textContent = "";
        confirmation.hidden = true;
        return;
      }
      const parts = optionDefinitions.map((option, index) => {
        const value = selectedOptionValues[index];
        if (!value) return "";
        if (option.position === Number(data?.surfacePosition)) return value;
        const displayName = option.position === Number(data?.sizePosition) ? "Size" : option.name;
        return `${displayName} ${value}`;
      });
      if (!selectedSize && exact.fulfilmentMode === "personal_fit") {
        parts.push(selectedFit || "PERSONAL FIT");
      }
      parts.push(exact.price);
      confirmation.textContent = parts.filter(Boolean).join(" · ");
      confirmation.hidden = false;
    };

    const whatsappText = (exact) => {
      const optionSummary = optionDefinitions.map((option, index) => {
        const value = selectedOptionValues[index];
        return value ? `${option.name} ${value}` : "";
      }).filter(Boolean).join(" · ");
      const priceText = exact?.price || priceForSurface(selectedSurfaceId, selectedSize);
      return `Hello ENSO, I’m interested in ${cleanText(data.publicName, 120)} — ${optionSummary} — ${priceText}. ${sameOriginSource(exact)}`;
    };

    const configureWhatsapp = (exact = lastExact) => {
      if (!whatsappAction) return;
      try {
        const url = new URL(data?.whatsappUrl || "");
        const isVerifiedHost = url.protocol === "https:"
          && url.hostname === "wa.me"
          && !url.username
          && !url.password
          && !url.port
          && !url.hash;
        const isDirectNumber = isVerifiedHost
          && /^\/\d+\/?$/.test(url.pathname)
          && !url.search;
        const hasProductContext = Boolean(
          cleanText(data?.publicName, 120)
          && (!surfaceRequired || selectedSurfaceId)
        );
        if (!isDirectNumber || !hasProductContext) {
          throw new Error("WhatsApp context is unavailable.");
        }
        url.searchParams.set("text", whatsappText(exact));
        whatsappAction.href = url.href;
        whatsappAction.dataset.ensoWhatsappValid = "true";
        whatsappAction.removeAttribute("aria-disabled");
        whatsappAction.removeAttribute("tabindex");
      } catch (error) {
        whatsappAction.removeAttribute("href");
        whatsappAction.setAttribute("aria-disabled", "true");
        whatsappAction.setAttribute("tabindex", "-1");
        delete whatsappAction.dataset.ensoWhatsappValid;
      }
    };

    let contextReturnFocus = null;
    const releaseContextLock = () => {
      if (!document.querySelector("[data-enso-context-dialog][open]")) {
        document.documentElement.classList.remove("enso-context-panel-open");
      }
    };

    const closeContext = () => {
      if (contextDialog?.open) contextDialog.close();
    };

    const openContext = (event) => {
      if (!contextDialog || typeof contextDialog.showModal !== "function"
        || !contextTitle || !contextBody) return;
      const trigger = event.currentTarget;
      const key = cleanText(trigger?.dataset.ensoContextOpen, 32);
      const template = contextTemplates.get(key);
      if (!template?.content) return;

      let fullUrl = "";
      const rawFullUrl = cleanText(template.dataset.ensoContextUrl, 2048);
      if (rawFullUrl) {
        try {
          const parsed = new URL(rawFullUrl, window.location.origin);
          if (parsed.origin === window.location.origin) fullUrl = parsed.href;
        } catch (error) {
          fullUrl = "";
        }
      }

      contextReturnFocus = trigger;
      if (contextEyebrow) {
        contextEyebrow.textContent = cleanText(template.dataset.ensoContextEyebrow, 80) || "ENSO · GUIDE";
      }
      contextTitle.textContent = cleanText(template.dataset.ensoContextTitle, 120) || "Guide";
      contextBody.replaceChildren(template.content.cloneNode(true));
      if (contextFullLink) contextFullLink.href = fullUrl || "#";
      if (contextFoot) contextFoot.hidden = !fullUrl;
      document.documentElement.classList.add("enso-context-panel-open");

      try {
        contextDialog.showModal();
        event.preventDefault();
        contextClose?.focus({ preventScroll: true });
      } catch (error) {
        contextBody.replaceChildren();
        releaseContextLock();
        contextReturnFocus = null;
      }
    };

    contextTriggers.forEach((trigger) => trigger.addEventListener("click", openContext));
    contextClose?.addEventListener("click", closeContext);
    contextDialog?.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeContext();
    });
    contextDialog?.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeContext();
    });
    contextDialog?.addEventListener("click", (event) => {
      if (event.target === contextDialog) closeContext();
    });
    contextDialog?.addEventListener("close", () => {
      contextBody?.replaceChildren();
      if (contextFoot) contextFoot.hidden = true;
      releaseContextLock();
      if (contextReturnFocus?.isConnected) contextReturnFocus.focus({ preventScroll: true });
      contextReturnFocus = null;
    });

    const commitCommerce = (addressMode = "replace") => {
      setError("");
      syncSizeOptions();
      setSelectedStates();
      renderSurfacePrices();
      if (fitOptions) fitOptions.hidden = true;
      if (fitSummary) {
        fitSummary.hidden = true;
        fitSummary.textContent = "";
      }

      const state = deriveCommerceState();
      lastExact = state.exact;
      const priceText = state.exact?.price || priceForSurface(selectedSurfaceId, selectedSize);
      if (price) {
        price.textContent = priceText;
        price.hidden = !priceText;
      }
      renderConfirmation(state.exact);
      updateAddress(state.exact, addressMode);
      resetActions();

      const fitMode = state.exact?.fulfilmentMode === "personal_fit";
      if (fitOptions) fitOptions.hidden = !fitMode;
      if (fitSummary && state.exact) {
        fitSummary.hidden = false;
        fitSummary.textContent = fitMode
          ? "Fit and final specification are confirmed personally."
          : "This object is offered in one size.";
      }

      if (state.action === "add") {
        if (variantInput) variantInput.value = state.exact.id;
        if (addAction) addAction.disabled = false;
        if (leadProperty && state.exact.fulfilmentMode === "standard_mto") {
          leadProperty.value = state.exact.leadTime;
          leadProperty.disabled = false;
        }
        if (addLabel) addLabel.textContent = state.label;
      } else if (state.action === "inquiry") {
        if (addAction) addAction.hidden = true;
        if (inquiryAction) {
          inquiryAction.hidden = false;
          inquiryAction.href = inquiryHref(state.exact, state.need);
        }
        if (inquiryLabel) inquiryLabel.textContent = state.label;
      } else {
        if (addLabel) addLabel.textContent = state.label;
      }
      if (actionNote) actionNote.textContent = state.note || "";
      if (guidanceInquiryAction) guidanceInquiryAction.href = guidanceInquiryHref(state.exact);
      configureWhatsapp(state.exact);
      return state;
    };

    const visibleGalleryItems = (gallery) => {
      if (!gallery) return [];
      const items = gallery.items.filter((item) => !gallery.broken.has(item.mediaId));
      const hasHero = items.some((item) => item.role === "hero");
      const fallback = gallery.fallbackHero;
      if (!hasHero && fallback && !gallery.broken.has(fallback.mediaId)
        && !items.some((item) => item.mediaId === fallback.mediaId)) {
        items.unshift(fallback);
      }
      return items.slice(0, maxGalleryItems);
    };

    const visibleGalleryPositions = (position, count) => {
      if (count <= 0) return [];
      const current = Math.max(0, Math.min(position, count - 1));
      if (count === 1) return [current];
      const neighbour = current < count - 1 ? current + 1 : current - 1;
      return [current, neighbour];
    };

    const syncGalleryDots = (count, position) => {
      if (!galleryDots) return;
      let dots = [...galleryDots.querySelectorAll("button")];
      if (dots.length !== count) {
        const fragment = document.createDocumentFragment();
        for (let index = 0; index < count; index += 1) {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.setAttribute("aria-label", `View product image ${index + 1} of ${count}`);
          dot.addEventListener("click", () => scrollGalleryTo(index));
          const core = document.createElement("span");
          core.setAttribute("aria-hidden", "true");
          dot.append(core);
          fragment.append(dot);
        }
        galleryDots.replaceChildren(fragment);
        dots = [...galleryDots.querySelectorAll("button")];
      }
      galleryDots.hidden = count <= 1;
      dots.forEach((dot, index) => {
        if (index === position) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    };

    const updateGalleryMeta = (gallery, item, position, announce = true) => {
      const items = visibleGalleryItems(gallery);
      const count = items.length;
      const safePosition = count ? Math.max(0, Math.min(position, count - 1)) : 0;
      if (galleryPrevious) galleryPrevious.disabled = count <= 1 || safePosition === 0;
      if (galleryNext) galleryNext.disabled = count <= 1 || safePosition === count - 1;
      syncGalleryDots(count, safePosition);
      if (announce && galleryStatus && item) {
        galleryStatus.textContent = `${gallery.surface} · ${item.label} · ${safePosition + 1} of ${count}`;
      }
    };

    const decodeImage = async (image) => {
      if (typeof image.decode === "function") await image.decode();
      else if (!image.complete || image.naturalWidth === 0) {
        await new Promise((resolve, reject) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", reject, { once: true });
        });
      }
      if (!image.naturalWidth) throw new Error("Image did not decode.");
      return image;
    };

    const createMediaImage = (item, source = item.src, useResponsive = true, eager = false) => {
      const image = new Image();
      image.alt = item.alt || `${cleanText(data.publicName, 120)} · ${item.label}`;
      image.decoding = "async";
      image.loading = eager ? "eager" : "lazy";
      if (useResponsive && item.sizes) image.sizes = item.sizes;
      if (useResponsive && item.srcset) image.srcset = item.srcset;
      image.src = source;
      return image;
    };

    const loadMediaItem = async (item) => {
      try {
        return await decodeImage(createMediaImage(item, item.src, true, true));
      } catch (error) {
        if (!item.fallbackSrc || item.fallbackSrc === item.src) return null;
        try {
          return await decodeImage(createMediaImage(item, item.fallbackSrc, false, true));
        } catch (fallbackError) {
          return null;
        }
      }
    };

    const createEmptyGalleryFrame = () => {
      const frame = document.createElement("figure");
      frame.className = "enso-object-product__media enso-object-product__media--empty";
      frame.dataset.ensoGalleryFrame = "";
      const placeholder = document.createElement("span");
      placeholder.className = "enso-object-product__media-placeholder";
      placeholder.setAttribute("aria-hidden", "true");
      frame.append(placeholder);
      return frame;
    };

    const bindGalleryFrameError = (frame, gallery, mediaId) => {
      const image = frame?.querySelector("img");
      if (!image || image.dataset.ensoErrorBound === "true") return;
      image.dataset.ensoErrorBound = "true";
      let failed = false;
      const handleFailure = () => {
        if (failed) return;
        failed = true;
        gallery?.broken.add(mediaId);
        frame.remove();
        window.requestAnimationFrame(() => updateCurrentGalleryPosition(false));
      };
      image.addEventListener("error", handleFailure, { once: true });
      if (image.complete && image.naturalWidth === 0) handleFailure();
    };

    const createGalleryFrame = (item, image, gallery) => {
      const frame = document.createElement("figure");
      frame.className = "enso-object-product__media";
      frame.dataset.ensoGalleryFrame = "";
      frame.dataset.ensoMediaId = item.mediaId;
      frame.dataset.ensoMediaRole = item.role;
      frame.append(image);
      bindGalleryFrameError(frame, gallery, item.mediaId);
      return frame;
    };

    const prepareGallery = async (
      surfaceId,
      generation,
      intent = { role: "", position: 0 }
    ) => {
      if (!galleryElement) return { surfaceId, gallery: null, fragment: null, reuse: true };
      const gallery = runtimeGalleries.get(surfaceId);
      const items = visibleGalleryItems(gallery);
      const existingFrames = [...galleryElement.querySelectorAll("[data-enso-gallery-frame]")];
      if (surfaceId === currentGallerySurfaceId && existingFrames.length) {
        const matchingRole = intent.role
          ? existingFrames.findIndex((frame) => frame.dataset.ensoMediaRole === intent.role)
          : -1;
        const intendedPosition = matchingRole >= 0
          ? matchingRole
          : Math.max(0, Math.min(intent.position, existingFrames.length - 1));
        const visiblePositions = visibleGalleryPositions(intendedPosition, existingFrames.length);
        const visibleImages = visiblePositions.map((position) => (
          existingFrames[position]?.querySelector("img")
        ));
        if (visibleImages.every(Boolean)) {
          try {
            await Promise.all(visibleImages.map(decodeImage));
            if (generation !== stateGeneration) return null;
            return { surfaceId, gallery, fragment: null, reuse: true };
          } catch (error) {
            // Rebuild from the governed gallery data below.
          }
        }
      }

      const fragment = document.createDocumentFragment();
      if (!gallery || !items.length) {
        fragment.append(createEmptyGalleryFrame());
        return { surfaceId, gallery, fragment, reuse: false };
      }

      const matchingRole = intent.role
        ? items.findIndex((item) => item.role === intent.role)
        : -1;
      const intendedPosition = matchingRole >= 0
        ? matchingRole
        : Math.max(0, Math.min(intent.position, items.length - 1));
      const visiblePositions = visibleGalleryPositions(intendedPosition, items.length);
      const visibleImages = await Promise.all(
        visiblePositions.map((position) => loadMediaItem(items[position]))
      );
      if (generation !== stateGeneration) return null;
      if (visibleImages.some((image) => !image)) {
        visibleImages.forEach((image, index) => {
          if (!image) gallery.broken.add(items[visiblePositions[index]].mediaId);
        });
        return { surfaceId, gallery, fragment: null, reuse: true, failed: true };
      }
      const decodedByPosition = new Map(
        visiblePositions.map((position, index) => [position, visibleImages[index]])
      );
      items.forEach((item, position) => {
        const image = decodedByPosition.get(position) || createMediaImage(item);
        fragment.append(createGalleryFrame(item, image, gallery));
      });
      return { surfaceId, gallery, fragment, reuse: false };
    };

    const captureGalleryIntent = () => {
      if (!galleryElement) return { role: "", position: 0 };
      const frames = [...galleryElement.querySelectorAll("[data-enso-gallery-frame]:not([hidden])")];
      if (!frames.length) return { role: "", position: 0 };
      const position = frames.reduce((nearest, frame, index) => (
        Math.abs(frame.offsetLeft - galleryElement.scrollLeft)
          < Math.abs(frames[nearest].offsetLeft - galleryElement.scrollLeft) ? index : nearest
      ), 0);
      return {
        role: cleanText(frames[position]?.dataset.ensoMediaRole, 32),
        position
      };
    };

    const commitPreparedGallery = (prepared, intent = { role: "", position: 0 }) => {
      if (!prepared || !galleryElement) return;
      if (!prepared.reuse && prepared.fragment) {
        galleryElement.replaceChildren(prepared.fragment);
      }
      currentGallerySurfaceId = prepared.surfaceId;
      galleryElement.dataset.ensoCurrentSurfaceId = prepared.surfaceId;
      const frames = [...galleryElement.querySelectorAll("[data-enso-gallery-frame]:not([hidden])")];
      frames.forEach((frame) => {
        bindGalleryFrameError(frame, prepared.gallery, normalizeId(frame.dataset.ensoMediaId));
      });
      const matchingRole = intent.role
        ? frames.findIndex((frame) => frame.dataset.ensoMediaRole === intent.role)
        : -1;
      currentGalleryPosition = matchingRole >= 0
        ? matchingRole
        : Math.max(0, Math.min(intent.position, frames.length - 1));
      galleryElement.scrollLeft = frames[currentGalleryPosition]?.offsetLeft || 0;
      const items = visibleGalleryItems(prepared.gallery);
      updateGalleryMeta(prepared.gallery, items[currentGalleryPosition] || items[0], currentGalleryPosition, false);
    };

    const updateCurrentGalleryPosition = (announce = false) => {
      if (!galleryElement) return;
      const frames = [...galleryElement.querySelectorAll("[data-enso-gallery-frame]:not([hidden])")];
      if (!frames.length) return;
      currentGalleryPosition = frames.reduce((nearest, frame, index) => (
        Math.abs(frame.offsetLeft - galleryElement.scrollLeft)
          < Math.abs(frames[nearest].offsetLeft - galleryElement.scrollLeft) ? index : nearest
      ), 0);
      const gallery = runtimeGalleries.get(currentGallerySurfaceId);
      const items = visibleGalleryItems(gallery);
      updateGalleryMeta(gallery, items[currentGalleryPosition], currentGalleryPosition, announce);
    };

    const scrollGalleryTo = (position) => {
      if (!galleryElement) return;
      const frames = [...galleryElement.querySelectorAll("[data-enso-gallery-frame]:not([hidden])")];
      if (!frames.length) return;
      const nextPosition = Math.max(0, Math.min(position, frames.length - 1));
      galleryElement.scrollTo({
        left: frames[nextPosition].offsetLeft,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
    };

    const scrollGallery = (delta) => scrollGalleryTo(currentGalleryPosition + delta);

    const requestOptionCommit = async (values, addressMode = "none") => {
      if (!resolverSupported) return;
      const nextTuple = normalizedOptionTuple(values);
      const surfaceIndex = optionIndexByPosition.get(Number(data?.surfacePosition));
      const nextSurfaceId = surfaceIndex === undefined ? "" : surfaceIdForName(nextTuple[surfaceIndex]);
      const galleryIntent = captureGalleryIntent();
      const generation = ++stateGeneration;
      root.setAttribute("aria-busy", "true");
      root.dataset.ensoStatePending = String(generation);
      const preparedGallery = await prepareGallery(nextSurfaceId, generation, galleryIntent);
      if (generation !== stateGeneration || !preparedGallery) return;
      if (preparedGallery.failed) {
        setSelectedStates();
        root.removeAttribute("aria-busy");
        delete root.dataset.ensoStatePending;
        return;
      }
      selectOptionTuple(nextTuple);
      commitPreparedGallery(preparedGallery, galleryIntent);
      commitCommerce(addressMode);
      root.removeAttribute("aria-busy");
      delete root.dataset.ensoStatePending;
    };

    const rehydrateFromLocation = () => {
      const nextState = stateFromLocation();
      void requestOptionCommit(nextState.options, "none");
    };

    optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        if (!resolverSupported) {
          try {
            const optionUrl = new URL(input.dataset.ensoOptionValueUrl || "", window.location.origin);
            const productUrl = new URL(data?.productUrl || window.location.pathname, window.location.origin);
            if (optionUrl.origin === window.location.origin && optionUrl.pathname === productUrl.pathname) {
              window.location.assign(optionUrl.href);
            }
          } catch (error) {
            window.location.reload();
          }
          return;
        }
        const optionIndex = optionIndexByPosition.get(Number(input.dataset.ensoOptionPosition));
        if (optionIndex === undefined) return;
        const nextTuple = [...selectedOptionValues];
        nextTuple[optionIndex] = cleanText(input.value, 120);
        if (!variantsForOptions(nextTuple).length) {
          optionDefinitions.forEach((option, index) => {
            if (index !== optionIndex && !variantsForOptions(nextTuple).length) nextTuple[index] = "";
          });
        }
        void requestOptionCommit(nextTuple, "replace");
      });
    });
    fitInputs.forEach((input) => {
      input.addEventListener("change", () => {
        selectedFit = input.value;
        commitCommerce("none");
      });
    });

    galleryPrevious?.addEventListener("click", () => {
      scrollGallery(-1);
    });
    galleryNext?.addEventListener("click", () => {
      scrollGallery(1);
    });
    let galleryScrollFrame = 0;
    galleryElement?.addEventListener("scroll", () => {
      if (galleryScrollFrame) window.cancelAnimationFrame(galleryScrollFrame);
      galleryScrollFrame = window.requestAnimationFrame(() => {
        galleryScrollFrame = 0;
        updateCurrentGalleryPosition(true);
      });
    }, { passive: true });
    galleryElement?.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      scrollGallery(event.key === "ArrowLeft" ? -1 : 1);
    });

    let formSubmitting = false;
    form?.addEventListener("submit", (event) => {
      if (!variantInput?.value || addAction?.disabled || addAction?.hidden) {
        event.preventDefault();
        setError("Select one verified configuration before adding it to the bag.");
        productError?.focus({ preventScroll: true });
        return;
      }
      if (formSubmitting) {
        event.preventDefault();
        return;
      }
      formSubmitting = true;
      if (addAction) addAction.disabled = true;
    });

    const handlePageShow = () => {
      formSubmitting = false;
      rehydrateFromLocation();
    };
    window.addEventListener("popstate", rehydrateFromLocation);
    window.addEventListener("pageshow", handlePageShow);

    configureWhatsapp();
    void requestOptionCommit(initialState.options, "initial");
    root.ensoProductDestroy = () => {
      stateGeneration += 1;
      window.removeEventListener("popstate", rehydrateFromLocation);
      window.removeEventListener("pageshow", handlePageShow);
      if (galleryScrollFrame) window.cancelAnimationFrame(galleryScrollFrame);
      closeContext();
      delete root.dataset.ensoInitialized;
      delete root.ensoProductDestroy;
      releaseContextLock();
    };
  };

  const initializeInquiry = (root) => {
    if (root.dataset.ensoInitialized === "true") return;
    root.dataset.ensoInitialized = "true";

    const params = new URLSearchParams(window.location.search);
    const productTupleKeys = [
      "object", "handle", "surface", "size", "size_label", "fit", "variant",
      "mode", "source", "surface_required", "size_required"
    ];
    const contractParamKeys = [...productTupleKeys, "need"];
    const hasDuplicateContractParam = contractParamKeys.some((key) => params.getAll(key).length > 1);
    const need = cleanText(params.get("need"), 40);
    const studioNeed = need === "studio_conversation";
    const guidanceNeed = need === "configuration_guidance";
    const allowedNeed = inquiryModes.has(need) ? need : "";
    const mode = cleanText(params.get("mode"), 40);
    const rawSurfaceRequired = params.get("surface_required");
    const rawSizeRequired = params.get("size_required");
    const outerVariantValues = params.getAll("variant");
    const outerVariantPresent = outerVariantValues.length > 0;
    const requiredFlagsAreValid = params.has("surface_required")
      && params.has("size_required")
      && /^(0|1)$/.test(rawSurfaceRequired || "")
      && /^(0|1)$/.test(rawSizeRequired || "");
    const surfaceRequired = rawSurfaceRequired === "1";
    const sizeRequired = rawSizeRequired === "1";
    const values = {
      object: cleanText(params.get("object"), 120),
      handle: cleanText(params.get("handle"), 120).replace(/[^a-z0-9-]/gi, ""),
      surface: cleanText(params.get("surface"), 120),
      size: cleanText(params.get("size"), 80),
      sizeLabel: cleanText(params.get("size_label"), 80) || "Size",
      fit: cleanText(params.get("fit"), 120),
      variant: outerVariantValues.length === 1 ? strictNumericId(outerVariantValues[0]) : ""
    };
    const contextInput = root.querySelector("[data-enso-inquiry-context]");
    const sourceInput = root.querySelector("[data-enso-inquiry-source]");
    const typeInput = root.querySelector("[data-enso-inquiry-type]");
    const summary = root.querySelector("[data-enso-inquiry-summary]");
    const eyebrow = root.querySelector("[data-enso-inquiry-eyebrow]");
    const intro = root.querySelector("[data-enso-inquiry-intro]");
    const title = root.querySelector("[data-enso-inquiry-title]");
    const submit = root.querySelector("[data-enso-inquiry-submit]");
    const contextError = root.querySelector("[data-enso-inquiry-context-error]");
    const success = root.querySelector("[data-enso-inquiry-success]");
    const formError = root.querySelector("[data-enso-inquiry-errors]");

    if (success) {
      window.requestAnimationFrame(() => success.focus({ preventScroll: true }));
      return;
    }
    root.dataset.inquiryMode = studioNeed ? "studio" : "standard";
    if (formError) window.requestAnimationFrame(() => formError.focus({ preventScroll: true }));

    const hasProductTuple = productTupleKeys.some((key) => params.has(key));
    if (studioNeed) {
      const studioIsValid = !hasDuplicateContractParam && !hasProductTuple;
      const studioCopy = cleanText(root.dataset.studioCopy, 320)
        || "Commissions, collaborations and selected creative projects.";
      if (eyebrow) eyebrow.textContent = root.dataset.studioEyebrow || "STUDIO INQUIRY";
      if (title) title.textContent = root.dataset.studioTitle || "Start a conversation.";
      if (intro) {
        intro.textContent = studioCopy;
        intro.hidden = false;
      }
      if (contextInput) contextInput.value = "Inquiry type: Studio conversation";
      if (typeInput) typeInput.value = "Studio conversation";
      if (sourceInput) sourceInput.value = `${window.location.origin}${window.location.pathname}`;
      if (submit) submit.disabled = !studioIsValid;
      if (contextError) {
        contextError.hidden = studioIsValid;
        contextError.textContent = studioIsValid
          ? ""
          : "Studio inquiry context is malformed. Return to Home and start the conversation again.";
      }
      return;
    }

    let source = "";
    let sourceIsProduct = false;
    let sourceVariant = "";
    let sourceVariantPresent = false;
    const rawSource = params.get("source");
    if (rawSource) {
      try {
        const parsed = new URL(rawSource, window.location.origin);
        const productPath = `/products/${values.handle}`;
        const sourceVariantValues = parsed.searchParams.getAll("variant");
        const sourceVariantCount = sourceVariantValues.length;
        sourceVariantPresent = sourceVariantCount > 0;
        sourceVariant = sourceVariantCount === 1 ? strictNumericId(sourceVariantValues[0]) : "";
        if (parsed.origin === window.location.origin
          && parsed.pathname.endsWith(productPath)
          && sourceVariantCount <= 1
          && (!sourceVariantPresent || sourceVariant)) {
          source = parsed.href;
          sourceIsProduct = true;
        }
      } catch (error) {
        source = "";
      }
    }

    const summaryParts = [
      values.object,
      values.surface,
      values.size && `${values.sizeLabel} ${values.size}`,
      values.fit && `Fit ${values.fit}`,
      values.variant && `Variant ${values.variant}`
    ].filter(Boolean);
    const contextParts = [
      values.object && `Object: ${values.object}`,
      values.handle && `Handle: ${values.handle}`,
      values.surface && `Surface: ${values.surface}`,
      values.size && `${values.sizeLabel}: ${values.size}`,
      values.fit && `Fit: ${values.fit}`,
      values.variant && `Variant ID: ${values.variant}`,
      mode && `Fulfilment mode: ${mode}`,
      need && `Intent: ${need}`
    ].filter(Boolean);

    if (summary) {
      summary.textContent = summaryParts.join(" · ");
      summary.hidden = summaryParts.length === 0;
    }
    if (contextInput) contextInput.value = contextParts.join(" · ");
    if (sourceInput) sourceInput.value = source;

    if (guidanceNeed) {
      if (eyebrow) eyebrow.textContent = root.dataset.guidanceEyebrow || "ENSO · OBJECT GUIDANCE";
      if (title) title.textContent = root.dataset.guidanceTitle || "Ask Enso.";
      if (intro) {
        intro.textContent = root.dataset.guidanceCopy
          || "Share what you are considering and ENSO will help clarify surface, size or fit.";
        intro.hidden = false;
      }
      if (typeInput) typeInput.value = "Product configuration guidance";
      const variantPairIsValid = outerVariantPresent || sourceVariantPresent
        ? Boolean(outerVariantPresent
          && sourceVariantPresent
          && values.variant
          && sourceVariant
          && values.variant === sourceVariant)
        : true;
      const sizePairIsValid = values.size || params.has("size_label")
        ? Boolean(values.size && params.has("size_label") && cleanText(params.get("size_label"), 80))
        : true;
      const contextIsValid = Boolean(
        !hasDuplicateContractParam
        && requiredFlagsAreValid
        && values.object
        && values.handle
        && sourceIsProduct
        && (!surfaceRequired || values.surface)
        && sizePairIsValid
        && variantPairIsValid
      );
      if (submit) submit.disabled = !contextIsValid;
      if (contextError) {
        contextError.hidden = contextIsValid;
        contextError.textContent = contextIsValid
          ? ""
          : "Configuration context is incomplete. Return to the product and ask ENSO again.";
      }
      return;
    }

    if (typeInput) typeInput.value = allowedNeed === "personal_fit"
      ? "Personal-fit product inquiry"
      : allowedNeed === "request_availability"
        ? "Product availability inquiry"
        : "";
    if (title) {
      title.textContent = allowedNeed === "personal_fit"
        ? (root.dataset.personalFitTitle || "Begin a personal-fit inquiry.")
        : allowedNeed === "request_availability"
          ? (root.dataset.requestTitle || "Request availability.")
          : (root.dataset.defaultTitle || "Begin an inquiry.");
    }

    const modeMatchesIntent = allowedNeed === "personal_fit"
      ? mode === "personal_fit"
      : allowedNeed === "request_availability"
        ? ["request_availability", "ready_stock", "standard_mto"].includes(mode)
        : false;
    const contextIsValid = Boolean(
      !hasDuplicateContractParam
      && requiredFlagsAreValid
      && allowedNeed
      && modeMatchesIntent
      && values.object
      && values.handle
      && values.variant
      && sourceIsProduct
      && sourceVariant === values.variant
      && (!surfaceRequired || values.surface)
      && (!sizeRequired || values.size)
    );
    if (submit) submit.disabled = !contextIsValid;
    if (contextError) {
      contextError.hidden = contextIsValid;
      contextError.textContent = contextIsValid
        ? ""
        : "Exact object context is incomplete. Return to the product and choose a configuration again.";
    }
  };

  const initialize = (scope = document) => {
    if (scope.matches?.("[data-enso-object-product]")) initializeProduct(scope);
    if (scope.matches?.("[data-enso-object-inquiry]")) initializeInquiry(scope);
    scope.querySelectorAll?.("[data-enso-object-product]").forEach(initializeProduct);
    scope.querySelectorAll?.("[data-enso-object-inquiry]").forEach(initializeInquiry);
  };

  initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const roots = [];
    if (event.target.matches?.("[data-enso-object-product]")) roots.push(event.target);
    event.target.querySelectorAll?.("[data-enso-object-product]").forEach((root) => roots.push(root));
    roots.forEach((root) => root.ensoProductDestroy?.());
    if (!document.querySelector("[data-enso-context-dialog][open]")) {
      document.documentElement.classList.remove("enso-context-panel-open");
    }
  });
})();
